// Chat sidebar — thread list, search, footer with user + model.
// Operates on the server's Session shape (id/name/updatedAt/messageCount).

import { Icon } from "../../icons";
import type { Me } from "../../lib/use-me";
import type { Session, SessionSearchHit } from "../../lib/rpc";
import { confirm } from "../../components/confirm";

interface SidebarProps {
  threads: Session[];
  active: string | null;
  loading: boolean;
  search: string;
  setSearch: (s: string) => void;
  setActive: (id: string, opts?: { scrollToIdx?: number; excerpt?: string }) => void;
  onNewThread: () => void;
  onDeleteThread: (id: string) => Promise<void>;
  onClose: () => void;     // mobile drawer close
  onOpenMenu: () => void;  // chat-actions menu
  dense: boolean;
  me: Me | null;
  model: string;
  /** Content-match hits (RAG / FTS) when search has 2+ chars. Threads
   *  whose id already appears in the title-filtered list are hidden
   *  here to avoid double-listing the same conversation. Empty until
   *  the debounced server call resolves. */
  contentHits: SessionSearchHit[];
  contentSearching: boolean;
}

export function ChatSidebar({
  threads, active, loading, search, setSearch, setActive, onNewThread,
  onDeleteThread, onClose, onOpenMenu, dense, me, model,
  contentHits, contentSearching,
}: SidebarProps) {
  const titleIds = new Set(threads.map((t) => t.id));
  const dedupedHits = contentHits.filter((h) => !titleIds.has(h.id));
  return (
    <aside className="chat-side">
      <div className="chat-side-hd">
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
          <Icon name="chat" size={16} />
          <span style={{ fontFamily: "var(--f-display)", fontWeight: 600, fontSize: 14 }}>
            Conversations
          </span>
        </div>
        <button className="icon-btn" onClick={onNewThread} title="New thread">
          <Icon name="plus" />
        </button>
        <button className="icon-btn mob-close-btn" onClick={onClose} title="Close">
          <Icon name="x" />
        </button>
      </div>

      <div style={{ padding: "0 var(--pad-3) var(--pad-3)" }}>
        <div className="chat-search">
          <Icon name="search" size={14} />
          <input
            placeholder="Search threads + messages…"
            value={search}
            onInput={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
          />
        </div>
      </div>

      <div className="chat-side-list">
        {loading && (
          <p style={{ color: "var(--text-dim)", fontSize: 11, padding: "var(--pad-3)" }}>
            loading…
          </p>
        )}
        {!loading && threads.length === 0 && !search && (
          <p
            style={{
              color: "var(--text-dim)", fontSize: 12,
              padding: "var(--pad-4) var(--pad-3)", textAlign: "center",
            }}
          >
            No conversations yet. Tap <Icon name="plus" size={12} /> to start one.
          </p>
        )}
        {!loading && threads.length > 0 && (
          <>
            <div className="chat-side-label">
              {search ? "Title matches" : "Recent"}
            </div>
            {threads.map((t) => (
              <ThreadRow
                key={t.id} t={t}
                active={active === t.id}
                onClick={() => setActive(t.id)}
                onDelete={async () => {
                  const ok = await confirm({
                    title: `Delete "${t.name || "Untitled"}"?`,
                    message: "The conversation and its message history will be removed. This can't be undone.",
                    confirmLabel: "Delete chat",
                    danger: true,
                  });
                  if (ok) await onDeleteThread(t.id);
                }}
              />
            ))}
          </>
        )}
        {/* Content matches (RAG/FTS). Only visible while searching;
          * threads that already showed up as title matches are
          * filtered out above. Empty section while the debounced
          * server call is in flight is fine — the spinner row
          * communicates progress without flashing "no matches". */}
        {search && (dedupedHits.length > 0 || contentSearching) && (
          <>
            <div className="chat-side-label">In messages</div>
            {contentSearching && dedupedHits.length === 0 && (
              <p style={{ color: "var(--text-dim)", fontSize: 11, padding: "var(--pad-2) var(--pad-3)" }}>
                searching…
              </p>
            )}
            {dedupedHits.map((h) => (
              <ContentHitRow
                key={`hit-${h.id}-${h.idx}`}
                h={h}
                active={active === h.id}
                onClick={() => setActive(h.id, { scrollToIdx: h.idx, excerpt: h.excerpt })}
              />
            ))}
          </>
        )}
        {!loading && search && threads.length === 0 && dedupedHits.length === 0 && !contentSearching && (
          <p
            style={{
              color: "var(--text-dim)", fontSize: 12,
              padding: "var(--pad-4) var(--pad-3)", textAlign: "center",
            }}
          >
            No matches for "{search}".
          </p>
        )}
      </div>

      <div className="chat-side-foot">
        <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
          {me?.initial ?? "?"}
        </div>
        {!dense && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12, fontWeight: 500,
                fontFamily: "var(--f-mono)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
            >
              {me?.username ?? "—"} · {me?.role ?? "—"}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "var(--text-dim)",
                fontFamily: "var(--f-mono)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
              title={model}
            >
              {model}
            </div>
          </div>
        )}
        <button className="icon-btn" title="Chat actions" onClick={onOpenMenu}>
          <Icon name="settings" />
        </button>
      </div>
    </aside>
  );
}

function ThreadRow({
  t, active, onClick, onDelete,
}: {
  t: Session; active: boolean; onClick: () => void; onDelete: () => void;
}) {
  return (
    <div className="thread" data-active={active} onClick={onClick}>
      <div className="thread-body">
        <div className="thread-title">{t.name || "Untitled"}</div>
        <div className="thread-meta">
          {formatRelative(t.updatedAt)} · {t.messageCount} msg
        </div>
      </div>
      {/* Delete button — visible on hover (desktop) and always on
        * mobile so a touch device can still reach it. The icon-btn
        * sits inside the .thread row so it still clicks through to
        * the row's onClick handler unless we stopPropagation. */}
      <button
        className="thread-delete icon-btn"
        title="Delete chat"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        type="button"
      >
        <Icon name="trash" size={13} />
      </button>
    </div>
  );
}

// Decode the entity-escaped + <mark>-wrapped excerpt from the server
// into a JSX node list. Server guarantees only <mark> tags survive its
// escape pass, so a simple split is safe — we don't have to handle
// nested or unknown tags. Built rather than dangerouslySetInnerHTML so
// we stay aligned with the "no innerHTML template literals" rule.
function renderExcerpt(html: string) {
  if (!html) return null;
  // Pull out <mark>…</mark> spans. The /g + capture splits the string
  // into [plain, marked, plain, marked, …] runs.
  const parts = html.split(/<mark>(.*?)<\/mark>/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <mark key={i}>{decodeEntities(part)}</mark>
      : <span key={i}>{decodeEntities(part)}</span>
  );
}

function decodeEntities(s: string): string {
  // Only the entities markHtmlExcerpt / highlightQueryInExcerpt produce.
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");
}

// Content-match row — looks like a thread row but renders the
// highlighted snippet underneath the title. Excerpt comes from the
// server already split into matched (<mark>) and plain runs; we just
// decode the escapes and render through JSX.
function ContentHitRow({
  h, active, onClick,
}: {
  h: SessionSearchHit; active: boolean; onClick: () => void;
}) {
  return (
    <div className="thread" data-active={active} onClick={onClick}>
      <div className="thread-body">
        <div className="thread-title">{h.name || "Untitled"}</div>
        <div className="thread-hit-excerpt">{renderExcerpt(h.excerpt || "")}</div>
        <div className="thread-meta">
          {formatRelative(h.updatedAt)} · {h.role === "assistant" ? "reply" : h.role} match
          {h.matchCount > 1 && <> · {h.matchCount} hits</>}
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(day / 365)}y`;
}
